import type { BadgeId, CodeLanguage, RewardEvent, RewardsSummary } from '@pebblecode/shared';

import { useEffect } from 'react';

import { api } from '../api/client.js';
import { createStore, useStore } from './store.js';

export type RewardToast =
  | { key: string; kind: 'points'; event: RewardEvent }
  | { key: string; kind: 'badge'; badge: BadgeId }
  | { key: string; kind: 'level'; level: number };

/** A toast before it gets its key (Omit distributed over the union). */
type ToastInput = RewardToast extends infer T
  ? T extends RewardToast
    ? Omit<T, 'key'>
    : never
  : never;

/** The browser's points and rewards (ADR 0010), refreshed after anything that can earn some. */
export const rewardsStore = createStore<RewardsSummary | null>(null);
export const rewardToastsStore = createStore<RewardToast[]>([]);

let toastSeq = 0;

function pushToasts(toasts: ToastInput[]): void {
  if (toasts.length === 0) return;
  const withKeys: RewardToast[] = toasts.map((t) => ({ ...t, key: `t${++toastSeq}` }));
  rewardToastsStore.set((list) => [...list, ...withKeys].slice(-4));
  for (const t of withKeys) {
    setTimeout(() => dismissRewardToast(t.key), 4500);
  }
}

export function dismissRewardToast(key: string): void {
  rewardToastsStore.set((list) => list.filter((t) => t.key !== key));
}

/** Compare with what we showed before and celebrate what is new (never on the first load). */
function apply(next: RewardsSummary): void {
  const prev = rewardsStore.get();
  rewardsStore.set(next);
  if (!prev) return;
  const seen = new Set(prev.recent.map((e) => e.id));
  const toasts: ToastInput[] = next.recent
    .filter((e) => !seen.has(e.id))
    .reverse()
    .map((event) => ({ kind: 'points' as const, event }));
  const hadBadges = new Set(prev.badges.map((b) => b.id));
  for (const b of next.badges)
    if (!hadBadges.has(b.id)) toasts.push({ kind: 'badge', badge: b.id });
  if (next.level > prev.level) toasts.push({ kind: 'level', level: next.level });
  pushToasts(toasts);
}

export async function refreshRewards(): Promise<void> {
  try {
    apply(await api.rewards());
  } catch {
    // Rewards are a bonus; a failed refresh must never disturb the editor.
  }
}

/** First look at each language: the server awards it once; the browser asks once per tab. */
const langsSent = new Set<CodeLanguage>();
export function rewardLanguageViewed(lang: CodeLanguage): void {
  if (langsSent.has(lang)) return;
  langsSent.add(lang);
  api
    .rewardAction({ kind: 'lang', lang })
    .then(apply)
    .catch(() => langsSent.delete(lang));
}

const stepSent = new Set<string>();
export function rewardStepped(projectId: string): void {
  if (stepSent.has(projectId)) return;
  stepSent.add(projectId);
  api
    .rewardAction({ kind: 'step', projectId })
    .then(apply)
    .catch(() => stepSent.delete(projectId));
}

/** Load the rewards once per page; later refreshes come from the actions that earn points. */
export function useRewards(): RewardsSummary | null {
  const summary = useStore(rewardsStore);
  useEffect(() => {
    if (!rewardsStore.get()) void refreshRewards();
  }, []);
  return summary;
}
